import React from 'react';
import { Button } from '@components/atoms/Button';
import { SourceBadge } from '@components/atoms/SourceBadge';
import { TaskMaterialStatusBadge } from '@components/atoms/TaskMaterialStatusBadge';
import { Checkbox } from '@components/atoms/Checkbox';
import { InputCurrency } from '@components/atoms/InputCurrency';
import { MaterialSummaryLine } from './MaterialSummaryLine';
import {
  TaskMaterial,
  TaskMaterialSummary,
  TaskMaterialUpdateInput,
  TaskMaterialStatus,
  MaterialSource,
  StockLevel,
} from '@models/warehouse.model';
import { Material } from '@models/material.model';
import { Add } from '@mui/icons-material';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CalculateIcon from '@mui/icons-material/Calculate';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { cn } from '@utils/cn';
import { LeadTimeBadge } from '@components/atoms/LeadTimeBadge';
import { StockBadge } from '@components/atoms/StockBadge';
import { getLeadTimeStatus } from '@utils/leadTime';
import { useTranslation } from '@hooks/useTranslation';

interface MaterialsSubTabProps {
  taskMaterials: TaskMaterial[];
  materialSummary?: TaskMaterialSummary;
  materialSummaryLoading?: boolean;
  materialSummaryError?: boolean;
  onRetrySummary?: () => void;
  stockLevels?: StockLevel[];
  isLoading: boolean;
  isDelegated: boolean;
  taskStartDate?: string | Date;
  onAddFromWarehouse: () => void;
  onOpenCalculator: () => void;
  onReturnMaterial: (material: TaskMaterial) => void;
  onDeleteMaterial: (material: TaskMaterial) => void;
  onUpdateMaterial: (input: TaskMaterialUpdateInput) => void;
  onReserveMaterial?: (material: TaskMaterial) => void;
  onConsumeMaterial?: (material: TaskMaterial) => void;
  manualMaterials?: Material[];
  onAddManually?: () => void;
  onEditManual?: (material: Material) => void;
  onDeleteManual?: (material: Material) => void;
}

const formatCurrency = (value: number) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const MaterialsSubTab: React.FC<MaterialsSubTabProps> = ({
  taskMaterials,
  materialSummary,
  materialSummaryLoading = false,
  materialSummaryError = false,
  onRetrySummary,
  stockLevels = [],
  isLoading,
  isDelegated,
  taskStartDate,
  onAddFromWarehouse,
  onOpenCalculator,
  onReturnMaterial,
  onDeleteMaterial,
  onUpdateMaterial,
  onReserveMaterial,
  onConsumeMaterial,
  manualMaterials = [],
  onAddManually,
  onEditManual,
  onDeleteManual,
}) => {
  const tTaskDetail = useTranslation('taskDetail');

  // Compute totals (warehouse)
  const warehouseCost = taskMaterials.reduce(
    (sum, m) => sum + m.quantity * (m.costPerUnit || 0),
    0,
  );
  const warehouseClientPrice = taskMaterials.reduce(
    (sum, m) => sum + (m.chargeToClient && m.pricePerUnit ? m.quantity * m.pricePerUnit : 0),
    0,
  );

  // Compute totals (manual)
  const manualCost = manualMaterials.reduce(
    (sum, m) => sum + (m.amount || 0) * (m.costPerUnit || 0),
    0,
  );
  const manualClientPrice = manualMaterials.reduce(
    (sum, m) => sum + (m.chargeToClient && m.pricePerUnit ? (m.amount || 0) * m.pricePerUnit : 0),
    0,
  );

  // Combined totals
  const totalCost = warehouseCost + manualCost;
  const totalClientPrice = warehouseClientPrice + manualClientPrice;
  const totalItemCount = taskMaterials.length + manualMaterials.length;
  const hasItems = totalItemCount > 0;

  // Sprint 34, story-176. Summary line's own item count is server-computed
  // (warehouseMaterialsCount + manualMaterialsCount from getTaskMaterialSummary), never a
  // server-provided itemCount field (doesn't exist on TaskMaterialSummaryDto) and never the local
  // taskMaterials/manualMaterials array lengths above — those drive the table/footer, which is a
  // separate, decoupled concern per docs/ux/story-176-design.md.
  const summaryItemCount = materialSummary
    ? materialSummary.warehouseMaterialsCount + materialSummary.manualMaterialsCount
    : 0;
  const showSummaryLine = materialSummaryLoading || materialSummaryError || summaryItemCount > 0;

  // Count planned materials for info
  const plannedCount = taskMaterials.filter(
    (m) => m.status === TaskMaterialStatus.PLANNED,
  ).length;

  // Lookup stock level for a given material
  const getStockForMaterial = (material: TaskMaterial): StockLevel | undefined => {
    if (!material.catalogId || !material.warehouseId) return undefined;
    return stockLevels.find(
      (sl) => sl.catalogId === material.catalogId && sl.warehouseId === material.warehouseId,
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{tTaskDetail.materials.title}</h3>
          {showSummaryLine && (
            <MaterialSummaryLine
              totalCost={materialSummary?.totalCost ?? 0}
              totalPrice={materialSummary?.totalPrice ?? 0}
              itemCount={summaryItemCount}
              isLoading={materialSummaryLoading}
              isError={materialSummaryError}
              onRetry={() => onRetrySummary?.()}
            />
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="tertiary"
            leftIcon={<CalculateIcon sx={{ fontSize: 18 }} />}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onOpenCalculator();
            }}>
            {tTaskDetail.materials.calculator}
          </Button>
          {onAddManually && (
            <Button
              variant="secondary"
              leftIcon={<NoteAddIcon sx={{ fontSize: 18 }} />}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onAddManually();
              }}>
              {tTaskDetail.materials.addManually}
            </Button>
          )}
          <Button
            leftIcon={<Add />}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onAddFromWarehouse();
            }}>
            {tTaskDetail.materials.addFromWarehouse}
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
        <InfoOutlinedIcon className="text-blue-500 mt-0.5" sx={{ fontSize: 18 }} />
        <p className="text-sm text-blue-700">
          {tTaskDetail.materials.infoBannerPrefix}{' '}
          <strong>{tTaskDetail.materials.infoBannerReserved}</strong>{' '}
          {tTaskDetail.materials.infoBannerMiddle}{' '}
          <strong>{tTaskDetail.materials.infoBannerPlanned}</strong>{' '}
          {tTaskDetail.materials.infoBannerSuffix}
        </p>
      </div>

      {/* Planned materials alert */}
      {plannedCount > 0 && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <InfoOutlinedIcon className="text-amber-500 mt-0.5" sx={{ fontSize: 18 }} />
          <p className="text-sm text-amber-700">
            <strong>{plannedCount}</strong>{' '}
            {plannedCount > 1
              ? tTaskDetail.materials.pendingReservationPlural
              : tTaskDetail.materials.pendingReservationSingular}
          </p>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-400 text-sm">{tTaskDetail.materials.loadingMaterials}</div>
      ) : hasItems ? (
        <div className="shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.materials.colSource}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.materials.colStatus}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.materials.colMaterial}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.materials.colQuantity}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.materials.colUnitCost}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.materials.colTotalCost}
                </th>
                {isDelegated && (
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {tTaskDetail.materials.colChargeClient}
                  </th>
                )}
                {isDelegated && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {tTaskDetail.materials.colClientPrice}
                  </th>
                )}
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.materials.colActions}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {/* Warehouse TaskMaterials */}
              {taskMaterials.map((material) => {
                const lineCost = material.quantity * (material.costPerUnit || 0);
                const isPlanned = material.status === TaskMaterialStatus.PLANNED;
                const isReserved = material.status === TaskMaterialStatus.RESERVED;
                const isConsumed = material.status === TaskMaterialStatus.CONSUMED;
                const isReturned = material.status === TaskMaterialStatus.RETURNED;

                return (
                  <tr
                    key={material.taskMaterialId}
                    className={cn(
                      'hover:bg-gray-50',
                      isPlanned && 'bg-amber-50/50',
                    )}>
                    {/* Source */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <SourceBadge source={material.source} size="sm" />
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {material.source === MaterialSource.WAREHOUSE && material.status ? (
                        <TaskMaterialStatusBadge status={material.status} size="sm" />
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Material Name */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-indigo-50 rounded flex items-center justify-center flex-shrink-0">
                          <WarehouseIcon className="text-indigo-400" sx={{ fontSize: 16 }} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {material.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {material.catalog?.sku
                              ? tTaskDetail.materials.skuLabel.replace('{sku}', material.catalog.sku)
                              : material.warehouse?.name || ''}
                          </p>
                          {/* Badge hierarchy for PLANNED materials */}
                          {isPlanned && material.leadTimeDays != null &&
                            material.leadTimeDays > 0 &&
                            taskStartDate && (() => {
                              const status = getLeadTimeStatus(
                                new Date(taskStartDate),
                                material.leadTimeDays
                              );
                              return status.alertLevel ? (
                                <LeadTimeBadge
                                  alertLevel={status.alertLevel}
                                  daysUntilDeadline={status.daysUntilDeadline}
                                  orderByDate={status.orderByDate}
                                />
                              ) : null;
                            })()}
                          {isPlanned &&
                            !(material.leadTimeDays != null && material.leadTimeDays > 0 && taskStartDate) &&
                            (() => {
                              const stock = getStockForMaterial(material);
                              const available = stock
                                ? Number(stock.quantityOnHand) - Number(stock.quantityReserved)
                                : 0;
                              return available <= 0 ? (
                                <StockBadge status="out_of_stock" size="sm" />
                              ) : null;
                            })()}
                          {/* In Stock badge for RESERVED materials */}
                          {isReserved && (
                            <StockBadge status="in_stock" size="sm" />
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Quantity */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                      {material.quantity} {material.unit || ''}
                    </td>

                    {/* Unit Cost */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(material.costPerUnit || 0)}
                    </td>

                    {/* Total Cost */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {formatCurrency(lineCost)}
                    </td>

                    {/* Charge Client (only if delegated) */}
                    {isDelegated && (
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex justify-center">
                          <Checkbox
                            checked={material.chargeToClient}
                            onChange={(checked) =>
                              onUpdateMaterial({
                                taskMaterialId: material.taskMaterialId,
                                chargeToClient: checked,
                              })
                            }
                          />
                        </div>
                      </td>
                    )}

                    {/* Client Price (only if delegated) */}
                    {isDelegated && (
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {material.chargeToClient ? (
                          <div className="w-28 ml-auto">
                            <InputCurrency
                              value={material.pricePerUnit ?? null}
                              onChange={(val) =>
                                onUpdateMaterial({
                                  taskMaterialId: material.taskMaterialId,
                                  pricePerUnit: val ?? undefined,
                                })
                              }
                              placeholder="0.00"
                            />
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    )}

                    {/* Actions */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-1">
                        {/* PLANNED: Reserve + Remove */}
                        {isPlanned && onReserveMaterial && (() => {
                          const stock = getStockForMaterial(material);
                          const available = stock
                            ? Number(stock.quantityOnHand) - Number(stock.quantityReserved)
                            : 0;
                          const canReserve = available >= Number(material.quantity);
                          return (
                            <button
                              type="button"
                              onClick={() => canReserve && onReserveMaterial(material)}
                              disabled={!canReserve}
                              className={cn(
                                'p-1.5 rounded transition-colors',
                                canReserve
                                  ? 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                                  : 'text-gray-300 cursor-not-allowed',
                              )}
                              title={
                                canReserve
                                  ? tTaskDetail.materials.reserveFromWarehouse
                                  : tTaskDetail.materials.insufficientStock.replace('{n}', String(available))
                              }>
                              <PlayArrowIcon sx={{ fontSize: 18 }} />
                            </button>
                          );
                        })()}

                        {/* RESERVED: Consume + Return */}
                        {isReserved && onConsumeMaterial && (
                          <button
                            type="button"
                            onClick={() => onConsumeMaterial(material)}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                            title={tTaskDetail.materials.markAsConsumed}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                          </button>
                        )}

                        {/* Return button: for RESERVED and CONSUMED */}
                        {(isReserved || isConsumed) &&
                          material.source === MaterialSource.WAREHOUSE && (
                            <button
                              type="button"
                              onClick={() => onReturnMaterial(material)}
                              className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                              title={tTaskDetail.materials.returnToWarehouseAction}>
                              <KeyboardReturnIcon sx={{ fontSize: 18 }} />
                            </button>
                          )}

                        {/* Delete: for PLANNED, RESERVED, CONSUMED (not RETURNED) */}
                        {!isReturned && (
                          <button
                            type="button"
                            onClick={() => onDeleteMaterial(material)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title={tTaskDetail.materials.removeMaterialAction}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {/* Manual Materials */}
              {manualMaterials.map((material) => {
                const lineCost = (material.amount || 0) * (material.costPerUnit || 0);
                const lineClientPrice =
                  material.chargeToClient && material.pricePerUnit
                    ? (material.amount || 0) * material.pricePerUnit
                    : 0;

                return (
                  <tr key={`manual-${material.materialId}`} className="hover:bg-gray-50">
                    {/* Source */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <SourceBadge source={MaterialSource.MANUAL} size="sm" />
                    </td>

                    {/* Status - N/A for manual */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-xs text-gray-400">—</span>
                    </td>

                    {/* Material Name */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                          <NoteAddIcon className="text-gray-400" sx={{ fontSize: 16 }} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {material.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {material.supplier || ''}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Quantity */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                      {material.amount} {material.amountType || ''}
                    </td>

                    {/* Unit Cost */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(material.costPerUnit || 0)}
                    </td>

                    {/* Total Cost */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {formatCurrency(lineCost)}
                    </td>

                    {/* Charge Client (only if delegated) */}
                    {isDelegated && (
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex justify-center">
                          <Checkbox
                            checked={material.chargeToClient || false}
                            disabled
                          />
                        </div>
                      </td>
                    )}

                    {/* Client Price (only if delegated) */}
                    {isDelegated && (
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                        {material.chargeToClient && material.pricePerUnit
                          ? formatCurrency(lineClientPrice)
                          : <span className="text-gray-400">—</span>}
                      </td>
                    )}

                    {/* Actions */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-1">
                        {onEditManual && (
                          <button
                            type="button"
                            onClick={() => onEditManual(material)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title={tTaskDetail.materials.editMaterialAction}>
                            <EditOutlinedIcon sx={{ fontSize: 18 }} />
                          </button>
                        )}
                        {onDeleteManual && (
                          <button
                            type="button"
                            onClick={() => onDeleteManual(material)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title={tTaskDetail.materials.removeMaterialAction}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 border-t border-gray-200">
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-3 text-sm font-semibold text-gray-700 text-right">
                  {tTaskDetail.materials.totals}
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                  {formatCurrency(totalCost)}
                </td>
                {isDelegated && <td className="px-4 py-3" />}
                {isDelegated && (
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                    {totalClientPrice > 0 ? formatCurrency(totalClientPrice) : '—'}
                  </td>
                )}
                <td className="px-4 py-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <WarehouseIcon className="text-gray-400" sx={{ fontSize: 24 }} />
          </div>
          <p className="text-sm">{tTaskDetail.materials.noMaterialsAdded}</p>
        </div>
      )}

      {/* Legend */}
      {hasItems && (
        <div className="flex items-center gap-4 text-xs text-gray-500">
          {taskMaterials.length > 0 && (
            <div className="flex items-center gap-1.5">
              <SourceBadge source={MaterialSource.WAREHOUSE} size="sm" />
              <span>{tTaskDetail.materials.legendStockUpdated}</span>
            </div>
          )}
          {manualMaterials.length > 0 && (
            <div className="flex items-center gap-1.5">
              <SourceBadge source={MaterialSource.MANUAL} size="sm" />
              <span>{tTaskDetail.materials.legendManual}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
